<template>
  <span>

    <b-container fluid>
      <b-row class="table-header">
        <b-col cols="9">
        </b-col>
        <b-col cols="12" lg="3">
          <b-form-group>
            <b-form-input
              ref="search"
              v-model="meta.search"
              :disabled="fetching"
              placeholder="Search by name"
              @keyup.native="search"
              @input="searchChanged"/>
          </b-form-group>
        </b-col>
      </b-row>

      <no-data v-if="noData" message="No topics found..."/>

      <b-table
        v-if="data.length"
        ref="table"
        :hover="rowClickEnabled && !fetching"
        :responsive="true"
        :busy="fetching"
        :items="data"
        :fields="formFields"
        :per-page="Number(meta.per_page)"
        :sort-by="meta.order"
        :sort-desc="meta.desc"
        :no-local-sorting="true"
        @sort-changed="sortChanged"
        @row-clicked="rowClicked">

        <template slot="name" slot-scope="data">
          <span class="name">
            {{ data.value }}
          </span>
        </template>

        <template slot="created" slot-scope="data">
          <div class="no-wrap">
            {{ readableDate(data.value) }}
          </div>
        </template>

        <template slot="updated" slot-scope="data">
          <div class="no-wrap">
            {{ readableDate(data.value) }}
          </div>
        </template>

        <template slot="status" slot-scope="data">
          <span :class="{'bad-value': data.value === 'inactive'}">
            {{ data.value }}
          </span>
        </template>

        <template slot="items" slot-scope="data">
          {{ data.value }}
        </template>

        <template slot="actions" slot-scope="data">
          <div v-if="archived" class="no-wrap">
            <span v-b-tooltip.hover style="display:inline-block" title="restore">
              <b-button
                :disabled="fetching"
                title="restore"
                variant="outline-secondary"
                @click="restore(data.item.id)">
                <i class="fas fa-check"></i>
              </b-button>
            </span>
          </div>
          <div v-else class="no-wrap">
            <span v-b-tooltip.hover style="display:inline-block" title="copy">
              <b-button
                :disabled="fetching"
                title="copy"
                variant="outline-secondary"
                @click.stop="copy(data.item.id)">
                <i class="fas fa-copy"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="move">
              <b-button
                :disabled="fetching"
                title="move"
                variant="outline-secondary"
                @click.stop="openMoveModal(data.item.id)">
                <i class="fas fa-arrow-alt-circle-right"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="settings">
              <b-button
                :disabled="fetching"
                title="settings"
                variant="outline-secondary"
                @click.stop="edit(data.item.id)">
                <i class="fas fa-cog"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="archive">
              <b-button
                :disabled="fetching"
                title="archive"
                variant="outline-secondary"
                @click.stop="archive(data.item.id)">
                <i class="fas fa-archive"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="move up">
              <b-button
                :disabled="fetching || disableMoveUp(data.item.id)"
                title="move up"
                variant="outline-secondary"
                @click.stop="reorder(data.item.id, 'decrease')">
                <i class="fas fa-arrow-up"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="move down">
              <b-button
                :disabled="fetching || disableMoveDown(data.item.id)"
                title="move down"
                variant="outline-secondary"
                @click.stop="reorder(data.item.id, 'increase')">
                <i class="fas fa-arrow-down"></i>
              </b-button>
            </span>
            <span v-b-tooltip.hover style="display:inline-block" title="knowledge check">
              <b-button
                :disabled="fetching"
                title="knowledge check"
                variant="outline-secondary"
                @click.stop="knowledgecheck(data.item.id)">
                <i class="fas fa-check-square"></i>
              </b-button>
            </span>
          </div>
        </template>

      </b-table>

      <b-row>
        <b-col cols="12">
          <b-pagination
            v-if="data.length"
            :disabled="fetching"
            :value="Number(meta.current_page)"
            :total-rows="Number(meta.total)"
            :per-page="Number(meta.per_page)"
            @change="pageChanged"/>
        </b-col>
      </b-row>

    </b-container>

    <b-modal
      v-model="showMoveModal"
      title="Move Topic"
      centered hide-footer
      size="lg">
      <move-entity
        selectable-type="component"
        @selected="move"/>
    </b-modal>

  </span>
</template>

<script>
import tableMixin from '../../mixins/admin-table'
import moveEntity from '../../../common/move-entity'

export default {
  components: {
    moveEntity
  },
  mixins: [tableMixin],
  data () {
    return {
      editRoute: 'admin-edit-topic',
      namespace: 'topics',
      routeParamName: 'topic',
      rowClickRoute: 'admin-items',
      formFields: [
        { key: 'name', label: 'Name' },
        { key: 'status', label: 'Status' },
        { key: 'items', label: 'Items' },
        { key: 'created', label: 'Created' },
        { key: 'updated', label: 'Updated' },
        { key: 'actions', label: 'Actions' }
      ],
      showMoveModal: false,
      IDToMove: null
    }
  },
  methods: {
    // override mixin onMount
    onMount () {
      this.$store.commit(`${this.namespace}/setMetaProp`, {
        view: this.view,
        prop: 'search',
        value: null
      })
      this.$store.dispatch(`${this.namespace}/indexNoPaginate`)
      this.fetch()
    },
    disableMoveUp(id) {
      return (
        this.meta.current_page === 1 &&
        this.data[0].id === id
      )
    },
    disableMoveDown(id) {
      return (
        this.meta.current_page === this.meta.last_page &&
        this.data[this.data.length - 1].id === id
      )
    },
    reorder (topicID, direction) {
      this.$store.dispatch('topics/reorder', {
        topicID,
        direction
      })
    },
    copy (id) {
      const accepted = confirm('This will copy this topic and all of its items. Continue?')
      if (accepted) {
        this.$store.dispatch(`${this.namespace}/copy`, id)
      }
    },
    openMoveModal (id) {
      this.$store.dispatch('client/getContentTree')
      this.IDToMove = id
      this.showMoveModal = true
    },
    move (node) {
      this.showMoveModal = false
      this.$store.dispatch('topics/move', {
        srcID: this.IDToMove,
        destID: node.id
      })
    },
    knowledgecheck(id){
      this.$router.push({name: 'admin-knowledgecheck', params: {topic: id}})
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/pages/admin/topics/table.vue